// src/pages/WorkHoursAnalytics.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { format, subDays, differenceInDays } from "date-fns";
import { useTranslation } from "react-i18next";

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  BarElement,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import Navbar from "../components/Navbar";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  BarElement
);

// ---------------- Helpers ----------------
const pad = (n) => String(n).padStart(2, "0");
const secondsToHMS = (sec) => {
  const s = sec % 60;
  const m = Math.floor((sec % 3600) / 60);
  const h = Math.floor(sec / 3600);
  return `${pad(h)}h ${pad(m)}m`;
};
const secondsToHoursDecimal = (sec) => Math.round((sec / 3600) * 100) / 100;

// ---------------- Component ----------------
export default function WorkHoursAnalytics({
  daysToShow = 30,
  theme,
  switchTheme,
}) {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const [workHours, setWorkHours] = useState({});

  useEffect(() => {
    if (!userId) return;
    const hoursRef = collection(db, "users", userId, "workHours");
    const unsub = onSnapshot(hoursRef, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data()?.seconds ?? 0;
      });
      setWorkHours(map);
    });
    return () => unsub();
  }, [userId]);

  // ----------------- Analytics Calculations -----------------
  const allDays = Object.keys(workHours).sort();
  const totalSeconds = Object.values(workHours).reduce((a, b) => a + b, 0);
  const averageSeconds = allDays.length > 0 ? totalSeconds / allDays.length : 0;
  const maxSeconds = Math.max(...Object.values(workHours), 0);
  const minSeconds = Math.min(...Object.values(workHours), 0);

  // Longest streak
  let streak = 0;
  let longestStreak = 0;
  const sortedDates = allDays.map((d) => new Date(d)).sort((a, b) => a - b);
  let prevDate = null;
  sortedDates.forEach((dateStr) => {
    const sec = workHours[format(dateStr, "yyyy-MM-dd")];
    if (sec > 0) {
      if (prevDate && differenceInDays(dateStr, prevDate) === 1) streak++;
      else streak = 1;
      if (streak > longestStreak) longestStreak = streak;
    } else streak = 0;
    prevDate = dateStr;
  });

  // Most productive weekdays
  const weekdayTotals = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  Object.entries(workHours).forEach(([day, seconds]) => {
    const d = new Date(day);
    weekdayTotals[d.getDay()] += seconds;
  });

  const weekdayNames = {
    0: t("days.sundayShort"),
    1: t("days.mondayShort"),
    2: t("days.tuesdayShort"),
    3: t("days.wednesdayShort"),
    4: t("days.thursdayShort"),
    5: t("days.fridayShort"),
    6: t("days.saturdayShort"),
  };

  const sortedWeekdays = Object.entries(weekdayTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([day, sec]) => {
      const name = weekdayNames[day];
      return { day: name, hours: secondsToHoursDecimal(sec) };
    });
  const topProductiveDays = sortedWeekdays
    .filter((d) => d.hours > 0)
    .slice(0, 5);

  // Last N days for charts
  const lastNDays = Array.from({ length: daysToShow }).map((_, i) =>
    subDays(new Date(), daysToShow - 1 - i)
  );

  const lineChartData = {
    labels: lastNDays.map((d) => format(d, "MMM d")),
    datasets: [
      {
        label: t("workHoursPage.chartLabel"),
        data: lastNDays.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          return secondsToHoursDecimal(workHours[key] ?? 0);
        }),
        borderColor: "#38bdf8",
        backgroundColor: "rgba(59, 131, 246, 0.2)",
        fill: true,
        tension: 0.3,
        pointRadius: 4,
      },
    ],
  };

  const barChartData = {
    labels: lastNDays.map((d) => format(d, "EEE")),
    datasets: [
      {
        label: t("workHoursPage.dailyHours"),
        data: lastNDays.map((d) =>
          secondsToHoursDecimal(workHours[format(d, "yyyy-MM-dd")] ?? 0)
        ),
        backgroundColor: "#f86538",
      },
    ],
  };

  return (
    <Container>
      <Navbar theme={theme} switchTheme={switchTheme} />
      <Header>
        <Title>{t("workHoursPage.title")}</Title>
        <Subtitle>{t("workHoursPage.subtitle")}</Subtitle>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatTitle>{t("workHoursPage.totalHours")}</StatTitle>
          <StatValue>{secondsToHoursDecimal(totalSeconds)}h</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>{t("workHoursPage.averagePerDay")}</StatTitle>
          <StatValue>{secondsToHoursDecimal(averageSeconds)}h</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>{t("workHoursPage.maxHoursDay")}</StatTitle>
          <StatValue>{secondsToHoursDecimal(maxSeconds)}h</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>{t("workHoursPage.longestStreak")}</StatTitle>
          <StatValue>
            {longestStreak} {t("workHoursPage.days")}
          </StatValue>
        </StatCard>
      </StatsGrid>

      <ChartsWrapper>
        <ChartBox>
          <ChartTitle>{t("workHoursPage.dailyHoursLine")}</ChartTitle>
          <Line
            data={lineChartData}
            options={{
              responsive: true,
              plugins: { legend: { display: false } },
            }}
          />
        </ChartBox>

        <ChartBox>
          <ChartTitle>{t("workHoursPage.dailyHoursBar")}</ChartTitle>
          <Bar
            data={barChartData}
            options={{
              responsive: true,
              plugins: { legend: { display: false } },
            }}
          />
        </ChartBox>
      </ChartsWrapper>
      <BottomItems>
        <SessionsList>
          <h3>{t("workHoursPage.topSessions")}</h3>
          <ul>
            {Object.entries(workHours)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([day, seconds]) => (
                <li key={day}>
                  {day}: {secondsToHMS(seconds)}
                </li>
              ))}
          </ul>
        </SessionsList>

        <ProductiveDaysCard>
          <h3>{t("workHoursPage.mostProductiveDays")}</h3>
          {topProductiveDays.length > 0 ? (
            <DaysList>
              {topProductiveDays.map((d) => (
                <li key={d.day}>
                  {d.day}: {d.hours}h
                </li>
              ))}
            </DaysList>
          ) : (
            <div>{t("workHoursPage.noData")}</div>
          )}
        </ProductiveDaysCard>
      </BottomItems>
    </Container>
  );
}

// ---------------- Styled ----------------
const Container = styled.div`
  padding: 0 2em 2em 2rem;
  color: var(--fontPrimaryColor);
  transition: 0.5s;
  @media (max-width: 700px) {
    padding: 10px;
  }
  @media (max-width: 650px) {
    margin-top: 120px;
  }
`;

const Header = styled.header`
  margin-bottom: 2.5rem;
  margin-left: 3%;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
`;

const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  transition: 0.5s;
  margin-bottom: 2rem;
  @media (max-width: 700px) {
    gap: 7px;
  }
`;

const BottomItems = styled.div`
  display: flex;
  gap: 2em;
  transition: 0.5s;
  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const StatCard = styled.div`
  background: var(--secondaryBackgroundColor);
  flex: 1;
  min-width: 150px;
  padding: 2em 1rem;
  border-radius: 12px;
  transition: 0.5s;
  text-align: center;
`;

const StatTitle = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 0.3rem;
  transition: 0.5s;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
`;

const ChartsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  transition: 0.5s;
  margin-bottom: 2rem;
`;

const ChartBox = styled.div`
  flex: 1;
  min-width: 300px;
  background: var(--secondaryBackgroundColor);
  padding: 1rem;
  border-radius: 12px;
  transition: 0.5s;
`;

const ChartTitle = styled.div`
  font-weight: 600;
  margin-bottom: 1rem;
`;

const SessionsList = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 40px;
  border-radius: 12px;
  transition: 0.5s;
  width: 50%;
  h3 {
    margin-bottom: 2rem;
    font-weight: 500;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 10px;
  }
  li {
    padding: 1em 0;
    border-bottom: 1px solid var(--fontThirdColor);
  }
  @media (max-width: 700px) {
    width: 100%;
  }
`;

const ProductiveDaysCard = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 1rem;
  border-radius: 12px;
  width: 50%;
  transition: 0.5s;
  padding: 40px;
  h3 {
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
  @media (max-width: 700px) {
    width: 100%;
  }
`;

const DaysList = styled.ul`
  list-style: none;
  transition: 0.5s;
  padding: 10px;
  margin: 0;
  li {
    padding: 1em 0;
    border-bottom: 1px solid var(--fontThirdColor);
  }
`;
